import React, { useMemo } from 'react';
import _get from 'lodash/get';
import styles from './index.module.scss';

import { If } from 'utils';
import { Config, UiKit } from 'components';
import Header from '../Header';
import ServiceOptions from './ServiceOptions';

const DATA_KEYS = {
  IS_ENABLED: 'is_enabled',
  CARRIER: 'carrier',
  SLA: 'sla'
};

export default function Preferred({
  data = null,
  isDisabled = false,
  carriers = [],
  onChange = () => {},
  errors = {},
  getErroKey = key => key
}) {
  const isEnabled = _get(data, 'is_enabled', false);
  const carrier = _get(data, 'carrier', null);
  const sla = _get(data, 'sla', null);

  const carrierError = _get(errors, getErroKey(DATA_KEYS.CARRIER), '');
  const slaError = _get(errors, getErroKey(DATA_KEYS.SLA), '');

  const carrierOptions = useMemo(() => {
    return carriers.reduce((acc, carrier) => {
      const id = _get(carrier, 'id', '');
      const carrierId = _get(carrier, 'carrier_id', '');
      const name =
        _get(carrier, 'name', '') + (carrierId ? `  (${carrierId})` : '');
      const isEnabled = _get(carrier, 'is_enabled', false);
      const option = { label: name, value: id };
      if (isEnabled) {
        acc.push(option);
      }
      return acc;
    }, []);
  }, [carriers]);

  const slaOptionsMap = useMemo(() => {
    return carriers.reduce((acc, carrier) => {
      const slas = _get(carrier, 'slas', []);
      const carrierId = _get(carrier, 'id', '');
      const isCarrierEnabled = _get(carrier, 'is_enabled', false);
      if (!(carrierId in acc)) {
        acc[carrierId] = [];
      }
      slas.forEach(sla => {
        const id = _get(sla, 'id', '');
        const name = _get(sla, 'name', '');
        const isActive = _get(sla, 'is_active', '');
        const option = { label: name, value: id };
        if (isActive && isCarrierEnabled) {
          acc[carrierId].push(option);
        }
      });
      return acc;
    }, {});
  }, [carriers]);

  const onTogglePreferredCarrier = value => {
    if (value === isEnabled) return;
    onChange({
      [DATA_KEYS.IS_ENABLED]: value,
      [DATA_KEYS.CARRIER]: null,
      [DATA_KEYS.SLA]: null
    });
  };

  const onCarrierChange = carrier => {
    onChange({
      [DATA_KEYS.IS_ENABLED]: true,
      [DATA_KEYS.CARRIER]: carrier,
      [DATA_KEYS.SLA]: null
    });
  };

  const onSlaChange = sla => {
    onChange({
      [DATA_KEYS.IS_ENABLED]: true,
      [DATA_KEYS.CARRIER]: carrier,
      [DATA_KEYS.SLA]: sla
    });
  };

  const selectedCarrierId = _get(carrier, 'value', '');
  const slaOptions = _get(slaOptionsMap, selectedCarrierId, []);

  return (
    <Config.Section columnClassName={styles.container}>
      <Header
        title="Preferred Carrier"
        description="Setup the default shipping service which will be automatically applied for all the return labels."
      />
      <UiKit.Grid gap="16" templateColumns="1fr">
        <ServiceOptions
          isEnabled={isDisabled ? null : isEnabled}
          isDisabled={isDisabled}
          onChange={onTogglePreferredCarrier}
        />
        <If test={isEnabled}>
          <UiKit.Grid gap="16" templateColumns="1fr 1fr">
            <UiKit.Select
              label="Carrier"
              value={carrier}
              options={carrierOptions}
              onChange={onCarrierChange}
              isDisabled={!isEnabled || isDisabled}
              error={carrierError}
              placeholder="eg. USPS"
            />
            <UiKit.Select
              label="SLA"
              value={sla}
              options={slaOptions}
              onChange={onSlaChange}
              isDisabled={!isEnabled || !carrier || isDisabled}
              error={slaError}
              placeholder="eg. USPS Ground Advantage"
            />
          </UiKit.Grid>
        </If>
      </UiKit.Grid>
    </Config.Section>
  );
}
